// src/features/workspace/fetchUserWorkspaces.ts
import { db } from '../../lib/firebase';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { Workspace } from './types';
import {
  DEFAULT_WORKSPACE_BASE_COLOR_KEY,
  isWorkspaceBaseColorKey,
} from '../../theme/workspaceBaseColors';

// 重複実行を防ぐためのキャッシュ
const fetchCache = new Map<string, Promise<Workspace[]>>();
const CACHE_TTL = 30 * 1000; // 30秒
const cacheTimestamps = new Map<string, number>();

export const invalidateWorkspaceFetchCache = (uid?: string): void => {
  if (uid) {
    const cacheKey = `workspaces_${uid}`;
    fetchCache.delete(cacheKey);
    cacheTimestamps.delete(cacheKey);
    return;
  }
  fetchCache.clear();
  cacheTimestamps.clear();
};

export const fetchUserWorkspaces = async (uid: string): Promise<Workspace[]> => {
  const fetchStartTime = performance.now();

  // 重複実行チェック
  const cacheKey = `workspaces_${uid}`;
  const now = Date.now();
  const cacheTimestamp = cacheTimestamps.get(cacheKey);

  if (fetchCache.has(cacheKey) && cacheTimestamp && (now - cacheTimestamp) < CACHE_TTL) {
    const cacheHitTime = performance.now();
    return fetchCache.get(cacheKey)!;
  }

  // Firestoreから取得
  const fetchPromise = (async () => {
    const workspaceCollectionRef = collection(db, `users/${uid}/workspaces`);

    try {
      const snapshot = await getDocs(workspaceCollectionRef);

      const workspaces: Workspace[] = [];

      // 並列取得用のPromise配列を作成
      const workspaceDetailPromises = snapshot.docs.map(async (docSnapshot) => {
        const workspaceId = docSnapshot.id;
        const userWorkspaceData = docSnapshot.data();

        try {
          const workspaceRef = doc(db, 'workspaces', workspaceId);
          const workspaceDoc = await getDoc(workspaceRef);

          if (workspaceDoc.exists()) {
            const workspaceData = workspaceDoc.data();

            // IDの整合性チェック
            if (workspaceData.id && workspaceData.id !== workspaceId) {
              console.warn(`📋 [fetchUserWorkspaces] ID不整合: 期待値=${workspaceId}, 実際=${workspaceData.id}`);
            }

            const workspace: Workspace = {
              id: workspaceId, // 常にドキュメントIDを使用
              name: workspaceData.name || workspaceId, // nameがない場合はIDを使用
              visibility: workspaceData.visibility || 0,
              publish: workspaceData.publish || false,
              publishId: workspaceData.publishId || undefined,
              baseColorKey: isWorkspaceBaseColorKey(workspaceData.baseColorKey)
                ? workspaceData.baseColorKey
                : DEFAULT_WORKSPACE_BASE_COLOR_KEY,
              companyId: workspaceData.companyId || undefined,
              billingAccountId: workspaceData.billingAccountId || undefined,
              planId: workspaceData.planId || undefined,
              seriesId: workspaceData.seriesId || undefined,
              offerId: workspaceData.offerId || undefined,
              entitlementOverrides: workspaceData.entitlementOverrides || undefined,
              stripeSubscriptionId: workspaceData.stripeSubscriptionId || undefined,
              stripeCustomerId: workspaceData.stripeCustomerId || undefined,
              stripeSubscriptionStatus: workspaceData.stripeSubscriptionStatus || undefined,
              billingStatus: workspaceData.billingStatus || undefined,
              cancelAtPeriodEnd: typeof workspaceData.cancelAtPeriodEnd === 'boolean' ? workspaceData.cancelAtPeriodEnd : undefined,
              currentPeriodEnd: workspaceData.currentPeriodEnd?.toDate ? workspaceData.currentPeriodEnd.toDate() : undefined,
              pastDueSince: workspaceData.pastDueSince?.toDate ? workspaceData.pastDueSince.toDate() : undefined,
              graceUntil: workspaceData.graceUntil?.toDate ? workspaceData.graceUntil.toDate() : undefined,
              planChangePendingPlanId: workspaceData.planChangePendingPlanId || undefined,
              iconPath: workspaceData.iconPath || undefined,
              order: userWorkspaceData.orderNo || userWorkspaceData.order || 0,
              createdBy: workspaceData.createdBy,
              createdAt: workspaceData.createdAt?.toDate(),
              updatedAt: workspaceData.updatedAt?.toDate(),
              parentWorkspaceId: workspaceData.parentWorkspaceId || undefined,
              notificationChannels: workspaceData.notificationChannels || undefined,
              notificationRules: workspaceData.notificationRules || undefined,
              crawlReferencedUrls: typeof workspaceData.crawlReferencedUrls === 'boolean' ? workspaceData.crawlReferencedUrls : undefined,
            };

            return workspace;
          } else {
            console.warn(`📋 [fetchUserWorkspaces] ワークスペース ${workspaceId} が見つかりません`);
            // 存在しないワークスペースでも、ユーザーが所属している場合は作成
            const workspace: Workspace = {
              id: workspaceId,
              name: workspaceId, // IDを名前として使用
              visibility: 0,
              publish: false,
              baseColorKey: DEFAULT_WORKSPACE_BASE_COLOR_KEY,
              iconPath: undefined,
              order: userWorkspaceData.orderNo || userWorkspaceData.order || 0,
              createdBy: uid,
            };
            return workspace;
          }
        } catch (error) {
          console.error(`📋 [fetchUserWorkspaces] ワークスペース詳細取得エラー: ${workspaceId}`, {
            error,
            code: (error as any)?.code,
            message: (error as any)?.message,
            workspaceId,
            uid
          });
          // エラー時は基本情報のみで作成
          const workspace: Workspace = {
            id: workspaceId,
            name: workspaceId,
            visibility: 0,
            publish: false,
            baseColorKey: DEFAULT_WORKSPACE_BASE_COLOR_KEY,
            iconPath: undefined,
            order: userWorkspaceData.orderNo || userWorkspaceData.order || 0,
            createdBy: uid,
          };
          return workspace;
        }
      });

      // 並列実行
      const workspaceResults = await Promise.all(workspaceDetailPromises);
      workspaces.push(...workspaceResults);

      workspaces.sort((a, b) => (a.order || 0) - (b.order || 0));

      return workspaces;
    } catch (error) {
      const errorTime = performance.now();
      console.error('📋 [fetchUserWorkspaces] エラー:', {
        error,
        uid,
        timestamp: new Date().toISOString(),
        elapsedTime: errorTime - fetchStartTime
      });
      throw error;
    }
  })();

  // Promiseをキャッシュに保存
  fetchCache.set(cacheKey, fetchPromise);
  cacheTimestamps.set(cacheKey, now);

  return fetchPromise;
};